import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {Box, Chip, Typography} from '@mui/material';

import {WorkforceProfileVersionEvolutionSkillList} from './WorkforceProfileVersionEvolutionSkillList';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {alignmentStatus, orangeColors} from '../../../../../layouts/theme/designSystem';
import {WorkforceProfileVersionEvolution} from '../../types/WorkforceProfileTypes';
import {
  formatVersionEvolutionDelta,
  formatVersionEvolutionScoreValue,
  getVersionEvolutionScoreDeltaEntries,
} from '../../utils/WorkforceProfileDetail/workforceVersionEvolutionUtils';

export interface Props {
  evolution: WorkforceProfileVersionEvolution;
}

export const WorkforceProfileVersionEvolutionPanel = ({evolution}: Props) => {
  const currentLanguage = useContext(LanguageContext);
  const {t} = useTranslation('translation', {
    keyPrefix: 'workforceProfilesPage.detail.versionEvolution',
  });
  const lang = currentLanguage === 'de' ? 'de' : 'en';

  const scoreKeys = getVersionEvolutionScoreDeltaEntries(evolution.scoreDeltas);

  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap'}}>
        <Typography variant='subtitle2' sx={{fontWeight: 600}}>
          {t('title')}
        </Typography>
        {evolution.baselineVersion != null && evolution.currentVersion != null && (
          <Chip
            label={t('versionRange', {
              from: evolution.baselineVersion,
              to: evolution.currentVersion,
            })}
            size='small'
            sx={{height: 24, fontWeight: 600, bgcolor: orangeColors[5], color: 'text.primary'}}
          />
        )}
      </Box>

      {evolution.summary.length > 0 && (
        <EmpionPaper
          sx={{
            border: `1px solid ${orangeColors[3]}`,
            borderRadius: '14px',
            p: 2.5,
            mb: 2,
          }}
        >
          <Typography variant='subtitle2' sx={{fontWeight: 600, mb: 1.5}}>
            {t('summaryTitle')}
          </Typography>
          <Box component='ul' sx={{m: 0, pl: 2.5}}>
            {evolution.summary.map((line) => (
              <Typography
                key={line}
                component='li'
                variant='body2'
                color='text.secondary'
                sx={{lineHeight: 1.65, mb: 0.75}}
              >
                {line}
              </Typography>
            ))}
          </Box>
        </EmpionPaper>
      )}

      {scoreKeys.length > 0 && (
        <EmpionPaper
          sx={{
            border: `1px solid ${orangeColors[3]}`,
            borderRadius: '14px',
            p: 2.5,
            mb: 2,
          }}
        >
          <Typography variant='subtitle2' sx={{fontWeight: 600, mb: 1.5}}>
            {t('scoreChangesTitle')}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 1,
            }}
          >
            {scoreKeys.map((key) => {
              const delta = evolution.scoreDeltas[key];
              if (!delta) {
                return null;
              }

              const previous = formatVersionEvolutionScoreValue(key, delta.previous);
              const current = formatVersionEvolutionScoreValue(key, delta.current);
              const deltaLabel = formatVersionEvolutionDelta(key, delta);
              const isNegative = (delta.delta ?? 0) < 0;

              return (
                <Box
                  key={key}
                  sx={{
                    border: `0.5px solid ${orangeColors[3]}`,
                    borderRadius: '10px',
                    p: 1.5,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography sx={{fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.75}}>
                    {t(`scoreLabels.${key}`)}
                  </Typography>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap'}}>
                    <Typography sx={{fontSize: 13, color: 'text.secondary'}}>
                      {previous ?? t('notAvailable')}
                    </Typography>
                    <ArrowForwardIcon sx={{fontSize: 14, color: 'text.disabled'}} />
                    <Typography sx={{fontSize: 13, fontWeight: 600}}>
                      {current ?? t('notAvailable')}
                    </Typography>
                    {deltaLabel && (
                      <Chip
                        icon={
                          isNegative ? (
                            <TrendingDownIcon sx={{fontSize: '14px !important'}} />
                          ) : (
                            <TrendingUpIcon sx={{fontSize: '14px !important'}} />
                          )
                        }
                        label={deltaLabel}
                        size='small'
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 600,
                          bgcolor: isNegative
                            ? alignmentStatus.gapChipBg
                            : alignmentStatus.metChipBg,
                          color: isNegative
                            ? alignmentStatus.gapAccent
                            : alignmentStatus.metAccent,
                        }}
                      />
                    )}
                    {!deltaLabel && previous != null && current != null && previous === current && (
                      <Chip
                        label={t('noChange')}
                        size='small'
                        sx={{height: 22, fontSize: 11, bgcolor: orangeColors[5]}}
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </EmpionPaper>
      )}

      <WorkforceProfileVersionEvolutionSkillList
        title={t('closedGapsTitle')}
        items={evolution.closedGaps}
        language={lang}
        variant='positive'
        showCount
      />

      <WorkforceProfileVersionEvolutionSkillList
        title={t('newlyMetSkillsTitle')}
        items={evolution.newlyMetSkills}
        language={lang}
        variant='positive'
        showCount
      />

      <WorkforceProfileVersionEvolutionSkillList
        title={t('newGapsTitle')}
        items={evolution.newGaps}
        language={lang}
        variant='warning'
      />

      <WorkforceProfileVersionEvolutionSkillList
        title={t('regressionsTitle')}
        items={evolution.regressions}
        language={lang}
        variant='negative'
      />
    </Box>
  );
};
